import { EyeInvisibleOutlined, EyeTwoTone, KeyOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Image, Input, Typography, message } from 'antd';
import axios from 'axios';
import { useState } from 'react';
import { IoLogInOutline } from 'react-icons/io5';
import { useHistory } from 'react-router-dom';
import techPackIcon from '../../assets/img/TechPack.svg';
import loginImage from '../../assets/img/login-image.png';
import resolvedLogo from '../../assets/img/pickpacknewlogo.svg';
import useStore from '../../store/useStore';
import { isHandledByInterceptor, toUserMessage } from '../../utils/apiError';
import './NewStyle.less';

const { Title, Text } = Typography;
const currentYear = new Date().getFullYear();

/** И-мэйл нь баталгаажаагүй бүртгэлд сервер буцаадаг төлөв */
const VERIFY_EMAIL_STATUS = 'verify-email';

const Login = () => {
  const history = useHistory();
  const setUser = useStore((state) => state.setUser);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  /** И-мэйл баталгаажаагүй бүртгэл — формын дээр анхааруулга харуулна */
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);

  /** Баталгаажуулах холбоосыг дахин илгээнэ */
  const resendVerifyLink = async () => {
    setResending(true);
    try {
      const { data } = await axios.post('/auth/resendVerifyEmailLink', {
        email: unverifiedEmail,
      });

      if (data?.statusCode !== 200) return;

      message.success('Бүртгэл баталгаажуулах холбоосыг и-мэйлээр илгээлээ.');
    } catch (e) {
      // Алдааны мэдэгдлийг axios interceptor гаргана
      console.log('Resend Verify Email Error: ', e.message);
    } finally {
      setResending(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    setUnverifiedEmail(null);
    try {
      // Нэвтрэх алдааг interceptor-ийн notification биш, antd message-ээр харуулна
      const response = await axios.post(`/auth/requesttoken`, values, { skipErrorToast: true });
      const data = response?.data;

      if (isHandledByInterceptor(data)) {
        message.error(toUserMessage(data));
        return;
      }

      /**
       * Сервер хоёр өөр хэлбэрээр хариулдаг: нэвтэрсэн хэрэглэгчийг `result`
       * дотор, и-мэйл баталгаажаагүй бүртгэлийг ХАРИУНЫ ДЭЭД ТҮВШИНД
       * (`result` wrapper-гүй) буцаадаг. Өмнө нь зөвхөн `data.result.status`-ыг
       * уншдаг байсан тул баталгаажаагүй бүртгэл дээр `data.result` нь
       * `undefined` болж TypeError үүсэж, catch дуугүй залгидаг — хэрэглэгчид
       * ямар ч мэдэгдэл гарахгүй, Нэвтрэх товч эвдэрсэн шиг харагддаг байв.
       */
      const payload = data?.result ?? data;
      const status = payload?.status;

      if (response.status === 200) {
        if (status === VERIFY_EMAIL_STATUS) {
          /**
           * Баталгаажаагүй бүртгэлд сервер JWT token өгдөг ч ХАДГАЛАХГҮЙ —
           * token байхад л Dashboard рүү оруулдаг тул баталгаажаагүй хэрэглэгч
           * системд нэвтрэх байсан. Оронд нь юу хийхийг зааж өгнө.
           */
          setUnverifiedEmail(payload?.email ?? values?.email ?? null);
          return;
        }

        if (status === 'registered-user') {
          if (data?.result?.merchantCodeList?.length) {
            // user is registered and has own merchant
            setUser({
              merchantCodeList: data?.result?.merchantCodeList ?? [],
              token: data?.result?.token ?? null,
              email: data?.result?.email ?? null,
              contractType: data?.result?.contractType ?? null,
              phoneNumber: data?.result?.phoneNumber ?? null,
              menu: data?.result?.menu ?? null,
              menuItems: data?.result?.menuItems ?? null,
              merchantCode: data?.result?.merchantCode ?? null,
              merchantName: data?.result?.merchantName ?? null,
              merchantLogo: data?.result?.merchantLogo ?? null,
              name: data?.result?.name ?? '',
              meta: data?.result?.meta ?? [],
              isB2B: data?.result?.isB2B ?? false,
              serviceType: data?.result?.serviceType ?? [],
              permission: data?.result?.permission ?? null,
              isAutoStockLowProcurement: data?.result?.autoStockLowProcurement ?? null,
            });
            history.push('/merchant/order/delivery');
          } else {
            // user is registered and has no merchant yet. So we have to check if user has sent merchant request
            const token = data?.result?.token ?? null;
            const isAutoStockLowProcurement = data?.result?.autoStockLowProcurement ?? null;
            const merchantsResponse = await axios.post(
              `/api/v1/me/merchants`,
              {},
              {
                headers: {
                  Authorization: token,
                },
              }
            );
            const merchantRequests = merchantsResponse?.data?.result ?? [];

            const autoFill = {
              name: data?.result?.name,
              email: data?.result?.email,
              phoneNumber: data?.result?.phoneNumber,
              contractType: data?.result?.contractType,
            };

            setUser({
              token,
              merchantRequests,
              autoFill,
              isAutoStockLowProcurement,
            });
          }
          message.success('Амжилттай нэвтэрлээ');
          return;
        }

        /**
         * Танихгүй төлөв — юу ч хийхгүй дуугүй өнгөрвөл хэрэглэгч товч
         * эвдэрсэн гэж ойлгоно. Тиймээс мэдэгдэл гаргана.
         */
        message.error('Нэвтрэхэд алдаа гарлаа. Дахин оролдоно уу.');
      }
    } catch (e) {
      // skipErrorToast тул interceptor мэдэгдэл гаргахгүй — энд харуулна
      console.log('Login Error: ', e);
      message.error(
        e?.response ? toUserMessage(e.response.data) : 'Нэвтрэхэд алдаа гарлаа. Дахин оролдоно уу.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fp-wrapper">
      {/* Left — cover image */}
      <div className="fp-cover">
        <div className="fp-cover-bg" style={{ backgroundImage: `url(${loginImage})` }} />
        <div className="fp-cover-overlay" />
      </div>

      {/* Right — form card */}
      <div className="fp-form-side">
        <div className="fp-form-main">
          <div className="fp-form-card">
            {/* Logo */}
            <div className="fp-logo-wrap">
              <Image src={resolvedLogo} className="fp-logo" alt="logo" preview={false} />
            </div>

            {/* Heading */}
            <div className="fp-heading">
              <Title level={3} className="fp-title">
                Тавтай морил
              </Title>
              <Text className="fp-subtitle">Баталгаажсан бүртгэлээ ашиглан нэвтрэнэ үү.</Text>
            </div>

            {/* И-мэйл баталгаажаагүй бүртгэл — юу хийхийг зааж өгнө */}
            {unverifiedEmail && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="И-мэйл хаягаа баталгаажуулаагүй байна"
                description={
                  <>
                    <div>
                      {unverifiedEmail} хаяг руу илгээсэн холбоосоор бүртгэлээ баталгаажуулсны дараа
                      нэвтрэх боломжтой болно.
                    </div>
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      loading={resending}
                      onClick={resendVerifyLink}
                    >
                      Баталгаажуулах холбоосыг дахин илгээх
                    </Button>
                  </>
                }
              />
            )}

            {/* Form */}
            <Form
              form={form}
              name="normal_login"
              layout="vertical"
              className="fp-form"
              labelAlign="left"
              initialValues={{ remember: true }}
              onFinish={onFinish}
            >
              <Form.Item
                name="email"
                label="Имэйл хаяг"
                rules={[
                  {
                    required: true,
                    message: 'Имэйл хаягаа оруулна уу!',
                  },
                  {
                    type: 'email',
                    message: 'Имэйл хаягийн формат буруу байна.',
                  },
                ]}
                hasFeedback
              >
                <Input size="large" prefix={<MailOutlined />} placeholder="Имэйл хаяг" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Нууц үг"
                rules={[{ required: true, message: 'Нууц үгээ оруулна уу!' }]}
              >
                <Input.Password
                  size="large"
                  prefix={<KeyOutlined />}
                  placeholder="Нууц үг"
                  autoComplete="current-password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              <div className="fp-forgot-password-link">
                <Button type="link" onClick={() => history.push('/forgot-password')}>
                  Нууц үгээ мартсан уу?
                </Button>
              </div>

              <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                <Button
                  type="primary"
                  size="large"
                  className="fp-submit-btn"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  icon={<IoLogInOutline size={16} />}
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  Нэвтрэх
                </Button>
                <div style={{ textAlign: 'center', marginTop: 30 }}>
                  <span style={{ color: '#8c8c8c', fontSize: 14 }}>Хэрэв та бүртгэлгүй бол</span>
                  <Button type="link" onClick={() => history.push('/register')}>
                    Шинэ бүртгэл үүсгэх
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </div>
        </div>

        {/* Footer */}
        <div className="fp-footer">
          <div className="fp-footer-brand">
            <span className="fp-powered">Powered by</span>
            <Image src={techPackIcon} preview={false} className="fp-techpack-logo" alt="TechPack" />
          </div>
          <span className="fp-copyright">© 2021-{currentYear} Зохиогчийн эрх хамгаалагдсан</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
